import { GRADE, SECTION } from '../../apis/admissions/admissions.interfaces';
import { TStudentPrimary } from '../../apis/students/students.interfaces';
import { GENDER } from '../../apis/users/users.interfaces';
import { mockUserAction } from './userAction';
import {
  TDocumentType,
  TDocumentStatus,
} from '../../apis/documents/documents.interfaces';
import { mockDocument } from '../documents/documentMock';

// Indian names - expanded to 100+ unique names for visual continuity with assessment mocks
const indianNames = [
  // Names matching academicAssessmentDetails.mock.ts for consistency
  ['Arjun', 'Sharma'], ['Priya', 'Patel'], ['Rohan', 'Kumar'], ['Ananya', 'Singh'], ['Vikram', 'Reddy'],
  // Additional common Indian names
  ['Ramesh', 'Agarwal'], ['Anjali', 'Patel'], ['Kavita', 'Reddy'], ['Rajesh', 'Mehta'], ['Meera', 'Iyer'],
  ['Amit', 'Gupta'], ['Sunita', 'Joshi'], ['Suresh', 'Nair'], ['Neha', 'Rao'], ['Karan', 'Desai'],
  ['Radha', 'Shah'], ['Anil', 'Verma'], ['Shreya', 'Agarwal'], ['Manoj', 'Sharma'], ['Divya', 'Patel'],
  ['Deepak', 'Kumar'], ['Pooja', 'Singh'], ['Ravi', 'Reddy'], ['Kiran', 'Mehta'], ['Nisha', 'Gupta'],
  ['Sandeep', 'Joshi'], ['Uma', 'Iyer'], ['Gopal', 'Nair'], ['Lakshmi', 'Rao'], ['Venkat', 'Desai'],
  ['Sarita', 'Shah'], ['Rohit', 'Verma'], ['Aditi', 'Agarwal'], ['Nikhil', 'Sharma'], ['Swati', 'Patel'],
  ['Kunal', 'Kumar'], ['Richa', 'Singh'], ['Varun', 'Reddy'], ['Tanvi', 'Mehta'], ['Harsh', 'Gupta'],
  ['Isha', 'Joshi'], ['Yash', 'Iyer'], ['Aryan', 'Nair'], ['Ananya', 'Rao'], ['Dhruv', 'Desai'],
  ['Ishita', 'Shah'], ['Rohan', 'Verma'], ['Siddharth', 'Agarwal'], ['Tanya', 'Sharma'], ['Vivek', 'Patel'],
  ['Zara', 'Kumar'], ['Aarav', 'Singh'], ['Ritika', 'Agarwal'], ['Kartik', 'Sharma'], ['Meghna', 'Patel'],
  ['Sanjana', 'Singh'], ['Vedant', 'Reddy'], ['Ishani', 'Mehta'], ['Arnav', 'Gupta'], ['Saanvi', 'Joshi'],
  ['Advik', 'Iyer'], ['Aadhya', 'Kapoor'], ['Aarush', 'Malhotra'], ['Aanya', 'Chopra'], ['Abhinav', 'Bansal'],
  ['Aditya', 'Saxena'], ['Ahaan', 'Tiwari'], ['Aishwarya', 'Bhatt'], ['Akash', 'Pandey'], ['Akshay', 'Yadav'],
  ['Amaira', 'Jain'], ['Amaya', 'Seth'], ['Anika', 'Khanna'], ['Anirudh', 'Dutta'], ['Anvi', 'Sinha'],
  ['Arnav', 'Bose'], ['Arya', 'Mukherjee'], ['Avni', 'Chakraborty'], ['Ayush', 'Ganguly'], ['Bhavya', 'Das'],
  ['Chaitanya', 'Roy'], ['Darsh', 'Banerjee'], ['Devansh', 'Basu'], ['Dhruv', 'Chatterjee'], ['Diya', 'Ghosh'],
  ['Esha', 'Guha'], ['Gauri', 'Mitra'], ['Harshit', 'Nandi'], ['Ishaan', 'Sengupta'], ['Jai', 'Bhattacharya'],
  ['Kaira', 'Dey'], ['Kavya', 'Mandal'], ['Krish', 'Pal'], ['Laksh', 'Sarkar'], ['Maya', 'Biswas'],
  ['Navya', 'Chowdhury'], ['Om', 'Dhar'], ['Parth', 'Goswami'], ['Reyansh', 'Kar'], ['Riya', 'Lahiri'],
  ['Rudra', 'Mazumdar'], ['Samaira', 'Mondal'], ['Samar', 'Nath'], ['Shivansh', 'Poddar'], ['Siya', 'Ray'],
  ['Tara', 'Sanyal'], ['Vansh', 'Sen'], ['Vihaan', 'Talukdar'], ['Yashvi', 'Thakur'], ['Zain', 'Varma'],
];

const indianCities = ['Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Kolkata', 'Hyderabad', 'Pune', 'Ahmedabad', 'Jaipur', 'Surat'];
const indianStates = ['Maharashtra', 'Delhi', 'Karnataka', 'Tamil Nadu', 'West Bengal', 'Telangana', 'Gujarat', 'Rajasthan'];
const indianPinCodes = ['400001', '110001', '560001', '600001', '700001', '500001', '411001', '380001', '302001', '395001'];

const grades = [
  GRADE.ONE, GRADE.TWO, GRADE.THREE, GRADE.FOUR, GRADE.FIVE, GRADE.SIX,
  GRADE.SEVEN, GRADE.EIGHT, GRADE.NINE, GRADE.TEN, GRADE.ELEVEN, GRADE.TWELVE,
];

const sections = [SECTION.A, SECTION.B, SECTION.C, SECTION.D, SECTION.E];

// Helper to distribute students more evenly across grades and sections for better mock data
function getGradeForIndex(index: number): GRADE {
  // Distribute students evenly - cycle through grades
  // With 100 students and 12 grades, this gives ~8 students per grade
  return grades[index % grades.length];
}

function getSectionForIndex(index: number, gradeIndex: number): SECTION {
  // Distribute sections evenly within each grade
  // This ensures when filtering by grade+section, we get multiple students
  // For each grade, distribute students across all sections
  // Use the index within the grade to determine section
  const studentsPerGrade = Math.ceil(100 / grades.length); // ~8-9 students per grade
  const indexWithinGrade = index % studentsPerGrade;
  return sections[indexWithinGrade % sections.length];
}

const categories = ['General', 'OBC', 'SC', 'ST', 'EWS'];

const documentTypes = [
  TDocumentType.BIRTH_CERTIFICATE,
  TDocumentType.AADHAR_CARD,
  TDocumentType.PAN_CARD,
  TDocumentType.PREVIOUS_MARKSHEET,
  TDocumentType.TRANSFER_CERTIFICATE,
];

function generateStudent(index: number): TStudentPrimary {
  const nameIndex = index % indianNames.length;
  const [firstName, lastName] = indianNames[nameIndex];
  const cityIndex = index % indianCities.length;
  const stateIndex = index % indianStates.length;
  const pinIndex = index % indianPinCodes.length;

  const gender = index % 2 === 0 ? GENDER.MALE : GENDER.FEMALE;
  const phoneNumber = `9${String(index).padStart(9, '0')}`.slice(0, 10);
  const email = `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`;

  // Use helper functions to distribute students more evenly across grades
  const grade = getGradeForIndex(index);
  const gradeIndex = grades.indexOf(grade);
  const section = getSectionForIndex(index, gradeIndex);
  const category = categories[index % categories.length];

  const baseDate = new Date('2020-01-01');
  const admissionDate = {
    ...mockUserAction,
    timestamp: Math.floor((baseDate.getTime() + index * 30 * 24 * 60 * 60 * 1000) / 1000),
  };

  const docType = documentTypes[index % documentTypes.length];
  const docNameMap: Record<TDocumentType, string> = {
    [TDocumentType.BIRTH_CERTIFICATE]: 'Birth Certificate',
    [TDocumentType.AADHAR_CARD]: 'Aadhar Card',
    [TDocumentType.PAN_CARD]: 'PAN Card',
    [TDocumentType.PREVIOUS_MARKSHEET]: 'Previous Marksheet',
    [TDocumentType.TRANSFER_CERTIFICATE]: 'Transfer Certificate',
    [TDocumentType.MEDICAL_CERTIFICATE]: 'Medical Certificate',
    [TDocumentType.SALARY_SLIP]: 'Salary Slip',
    [TDocumentType.RESIGNATION_LETTER]: 'Resignation Letter',
    [TDocumentType.JOINING_LETTER]: 'Joining Letter',
    [TDocumentType.PROMOTION_LETTER]: 'Promotion Letter',
    [TDocumentType.LEAVE_LETTER]: 'Leave Letter',
    [TDocumentType.PHOTO]: 'Photo',
    [TDocumentType.OTHER]: 'Other Document',
    [TDocumentType.HR_DOCUMENT]: 'HR Document',
  };

  return {
    id: String(9000000000 + index),
    basicDetails: {
      firstName,
      middleName: index % 3 === 0 ? 'Kumar' : '',
      lastName,
      gender,
      profile: {
        profilePicture: mockDocument,
        idProof: mockDocument,
        addressProof: mockDocument,
      },
    },
    actions: {
      created: mockUserAction,
    },
    contactDetails: {
      phone: {
        countryCode: '+91',
        phoneNumber,
        isVerified: index % 2 === 0,
      },
      email: {
        email,
        isVerified: true,
      },
      address: {
        address1: `${(index % 100) + 1} ${indianCities[cityIndex]} Street`,
        address2: index % 3 === 0 ? `Apartment ${String.fromCharCode(65 + (index % 5))}-${(index % 10) + 1}` : '',
        city: indianCities[cityIndex],
        state: indianStates[stateIndex],
        country: 'India',
        postalCode: indianPinCodes[pinIndex],
      },
    },
    documents: [
      {
        fileUrl: `https://example.com/${docType}-${index}.pdf`,
        documentName: `${docNameMap[docType]}-${firstName}-${lastName}.pdf`,
        description: `${docNameMap[docType]} for ${firstName} ${lastName}`,
        uploadedAt: mockUserAction,
        documentId: String(100000000 + index),
        userId: String(9000000000 + index),
        documentType: docType,
        submissionDate: '2021-01-01',
        status: TDocumentStatus.SUBMITTED,
      },
    ],
    admissionDate,
    grade,
    gradeId: String(grade),
    category,
    section,
  };
}

export const mockUserDetailsList: TStudentPrimary[] = Array.from({ length: 100 }, (_, i) => generateStudent(i));
